// src/redux/reactionsSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toggleReaction, getReactionSummary } from "../services/reactionsService";

const normId = (v) => String(v ?? "").replace(/[\u0000-\u001F\u007F]/g, "").trim();
const lower = (v) => normId(v).toLowerCase();

const safeCounts = (counts) => {
  if (!counts || typeof counts !== "object") return null;
  const out = {};
  for (const [k, v] of Object.entries(counts)) {
    const n = Number(v);
    if (Number.isFinite(n)) out[String(k)] = n;
  }
  return out;
};

const ensure = (state, creatorId) => {
  const id = normId(creatorId);
  if (!id) return null;

  if (!state.byCreatorId[id]) {
    state.byCreatorId[id] = {
      userReaction: null,
      counts: {},
      loadingSummary: false,
      toggling: false,
      // convenience flag (so components can just use node.loading)
      loading: false,
      error: null,
      summaryRequestId: null,

      // rollback support for optimistic toggles
      _optimistic: null, // { prevReaction, prevCounts }
    };
  }
  return state.byCreatorId[id];
};

// Apply an optimistic toggle (adjust counts + userReaction)
const applyOptimisticToggle = (node, rt) => {
  const prevReaction = node.userReaction || null;
  const prevCounts = { ...(node.counts || {}) };

  const nextCounts = { ...(node.counts || {}) };
  const inc = (k, d) => {
    const cur = Number(nextCounts[k] ?? 0) || 0;
    const next = cur + d;
    nextCounts[k] = next < 0 ? 0 : next;
  };

  // toggling same reaction -> clears it
  const nextReaction = prevReaction === rt ? null : rt;

  // decrement previous if existed
  if (prevReaction) inc(prevReaction, -1);
  // increment next if set
  if (nextReaction) inc(nextReaction, +1);

  node.userReaction = nextReaction;
  node.counts = nextCounts;
  node._optimistic = { prevReaction, prevCounts };
};

export const toggleReactionAsync = createAsyncThunk(
  "reactions/toggle",
  async (
    { creatorId, reactionType, surface, platform, prevReaction } = {},
    { rejectWithValue }
  ) => {
    const id = normId(creatorId);
    const rt = lower(reactionType);
    const surf = normId(surface) || "discover";
    const plat = lower(platform) || "instagram";

    if (!id) return rejectWithValue({ message: "Missing creatorId", creatorId: id });
    if (!rt) return rejectWithValue({ message: "Missing reactionType", creatorId: id });

    try {
      // Pass context through (backend can ignore if not used yet)
      const data = await toggleReaction({
        creatorId: id,
        reactionType: rt,
        surface: surf,
        platform: plat,
        prevReaction: prevReaction ?? null,
      });

      // Normalise expected shape
      return (
        data || {
          creatorId: id,
          userReaction: rt,
          counts: {},
        }
      );
    } catch (err) {
      return rejectWithValue({
        message: err?.message || "Failed to react.",
        creatorId: id,
      });
    }
  }
);

// required for initial render on cards (or quick view)
export const fetchReactionSummaryAsync = createAsyncThunk(
  "reactions/getSummary",
  async ({ creatorId } = {}, { rejectWithValue }) => {
    const id = normId(creatorId);
    if (!id) return rejectWithValue({ message: "Missing creatorId", creatorId: id });

    try {
      const data = await getReactionSummary(id);
      return data || { creatorId: id };
    } catch (err) {
      return rejectWithValue({
        message: err?.message || "Failed to load reactions.",
        creatorId: id,
      });
    }
  }
);

const initialState = {
  byCreatorId: {}, // { [id]: { userReaction, counts, loadingSummary, toggling, loading, error, summaryRequestId, _optimistic } }
};

const reactionsSlice = createSlice({
  name: "reactions",
  initialState,
  reducers: {
    primeReactions(state, action) {
      const { creatorId, userReaction, counts } = action.payload || {};
      const node = ensure(state, creatorId);
      if (!node) return;

      if (userReaction !== undefined) node.userReaction = userReaction ?? null;

      const c = safeCounts(counts);
      if (c) node.counts = { ...(node.counts || {}), ...c };
    },
    clearReactionError(state, action) {
      const id = normId(action.payload?.creatorId);
      if (!id) return;
      const node = ensure(state, id);
      if (node) node.error = null;
    },
  },
  extraReducers: (builder) => {
    // ---- TOGGLE ----
    builder
      .addCase(toggleReactionAsync.pending, (state, action) => {
        const id = normId(action.meta.arg?.creatorId);
        const rt = lower(action.meta.arg?.reactionType);
        const node = ensure(state, id);
        if (!node || !rt) return;

        node.toggling = true;
        node.loading = true;
        node.error = null;

        // ✅ optimistic: instant UI
        applyOptimisticToggle(node, rt);
      })
      .addCase(toggleReactionAsync.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const creatorId = normId(payload.creatorId || payload.creator_id || action.meta.arg?.creatorId);
        const node = ensure(state, creatorId);
        if (!node) return;

        node.toggling = false;
        node.loading = false;
        node.error = null;
        node._optimistic = null;

        // Backend is source of truth if provided
        if (payload.userReaction !== undefined || payload.user_reaction !== undefined) {
          node.userReaction = payload.userReaction ?? payload.user_reaction ?? null;
        }

        const c = safeCounts(payload.counts);
        if (c) node.counts = c;
      })
      .addCase(toggleReactionAsync.rejected, (state, action) => {
        const p = action.payload || {};
        const creatorId = normId(p.creatorId || action.meta.arg?.creatorId);
        const node = ensure(state, creatorId);
        if (!node) return;

        node.toggling = false;
        node.loading = false;

        // ✅ rollback optimistic update
        if (node._optimistic) {
          node.userReaction = node._optimistic.prevReaction ?? null;
          node.counts = node._optimistic.prevCounts || {};
          node._optimistic = null;
        }

        node.error = p.message || action.error?.message || "Failed to react.";
      });

    // ---- GET SUMMARY ----
    builder
      .addCase(fetchReactionSummaryAsync.pending, (state, action) => {
        const node = ensure(state, action.meta.arg?.creatorId);
        if (!node) return;
        node.loadingSummary = true;
        node.loading = true;
        node.error = null;
        node.summaryRequestId = action.meta.requestId;
      })
      .addCase(fetchReactionSummaryAsync.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const creatorId = normId(payload.creatorId || payload.creator_id || action.meta.arg?.creatorId);
        const node = ensure(state, creatorId);
        if (!node) return;

        // ignore stale summary responses
        if (node.summaryRequestId && node.summaryRequestId !== action.meta.requestId) return;

        node.loadingSummary = false;
        node.loading = node.toggling; // keep true if toggle in flight
        node.summaryRequestId = null;
        node.error = null;

        if (payload.userReaction !== undefined || payload.user_reaction !== undefined) {
          node.userReaction = payload.userReaction ?? payload.user_reaction ?? null;
        }

        const c = safeCounts(payload.counts);
        if (c) node.counts = c;
      })
      .addCase(fetchReactionSummaryAsync.rejected, (state, action) => {
        const p = action.payload || {};
        const creatorId = normId(p.creatorId || action.meta.arg?.creatorId);
        const node = ensure(state, creatorId);
        if (!node) return;

        if (node.summaryRequestId && node.summaryRequestId !== action.meta.requestId) return;

        node.loadingSummary = false;
        node.loading = node.toggling;
        node.summaryRequestId = null;
        node.error = p.message || action.error?.message || "Failed to load reactions.";
      });
  },
});

export const { primeReactions, clearReactionError } = reactionsSlice.actions;

export const selectReactionsForCreator = (s, creatorId) => {
  const id = normId(creatorId);
  return (
    s.reactions?.byCreatorId?.[id] || {
      userReaction: null,
      counts: {},
      loadingSummary: false,
      toggling: false,
      loading: false,
      error: null,
    }
  );
};

export const selectReaction = selectReactionsForCreator;

export default reactionsSlice.reducer;